import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Card, CardDiscardReasonEnum, CustomerService, DISCARD_REASON_LABEL, isPlatformLossReason, LossReason, LossReasonService } from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { LossReasonFormModalComponent } from '../loss-reason-form-modal/loss-reason-form-modal.component';

export interface SelectLossReasonData {
  card?: Card;
  /**
   * False where a platform discard cannot be persisted: a deal created straight into a
   * lost stage goes through the generic create, which only knows `lossReason` (E3 ADR-001).
   */
  allowPlatform?: boolean;
}

/** What the dialog closes with. The note exists only for a platform discard reason (E3 RF-24). */
export interface SelectLossReasonResult {
  idLossReason: string;
  lossNote?: string;
}

@Component({
    selector: 'app-select-loss-reason',
    templateUrl: './select-loss-reason.component.html',
    styleUrl: './select-loss-reason.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SelectLossReasonComponent extends AbstractComponent {
  public lossReasonList: Array<LossReason> = [];
  /** Open deals a blacklist discard withdraws (E3 RF-26); null until known. */
  public blacklistImpact: number | null = null;

  private readonly card?: Card;
  private readonly allowPlatform: boolean;

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<SelectLossReasonComponent>,
    @Inject(MAT_DIALOG_DATA) data: SelectLossReasonData | Card,
    private lossReasonService: LossReasonService,
    private customerService: CustomerService,
  ) {
    super(injector);
    // Callers used to pass the card itself; both shapes are accepted.
    this.card = (data as SelectLossReasonData)?.card ?? (data as Card);
    this.allowPlatform = (data as SelectLossReasonData)?.allowPlatform !== false;
    this.setupForm();
  }

  ngOnInit(): void {
    this.getList();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      lossReason: [null, [Validators.required]],
      lossNote: [''],
    });

    this.formGroup.get('lossReason')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadBlacklistImpact());
  }

  getList(): void {
    this.lossReasonService.getList().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: Array<LossReason>) => {
        this.lossReasonList = response.filter((item) => this.isOffered(item));
        const current = this.card?.lossReason?._id ?? this.card?.lossReasonKey;
        if (current && !this.formGroup.get('lossReason')?.value && this.canOfferBlacklist(current)) {
          this.formGroup.get('lossReason')?.setValue(current);
        }
      },
    });
  }

  /** Platform discards only where they can be persisted; the blacklist one only for who may blacklist. */
  private isOffered(item: LossReason): boolean {
    if (!this.allowPlatform && isPlatformLossReason(item)) return false;
    return this.canOfferBlacklist(item._id);
  }

  /** E3 RF-22: blacklisting rides on the `blockContact` permission — the API refuses it with 403 otherwise. */
  private canOfferBlacklist(idLossReason: string): boolean {
    return idLossReason !== CardDiscardReasonEnum.BLACKLIST || this.canBlacklist();
  }

  get selectedIsPlatform(): boolean {
    return isPlatformLossReason({ _id: this.formGroup.get('lossReason')?.value });
  }

  get selectedIsBlacklist(): boolean {
    return this.formGroup.get('lossReason')?.value === CardDiscardReasonEnum.BLACKLIST;
  }

  discardLabel(item: LossReason): string {
    return DISCARD_REASON_LABEL[item._id as CardDiscardReasonEnum] ?? item.name;
  }

  /** RF-26/AC-20: the user is told how many deals the blacklist withdraws before confirming. */
  private loadBlacklistImpact(): void {
    const idCustomer = this.card?.customer?._id;
    if (!this.selectedIsBlacklist || !idCustomer) {
      this.blacklistImpact = null;
      return;
    }
    this.customerService.getBlacklistImpact(idCustomer).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => (this.blacklistImpact = result?.deals ?? 0),
      error: () => (this.blacklistImpact = null),
    });
  }

  save(): void {
    this.loading = true;

    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      this.loading = false;
      return;
    }

    const rawValue = this.formGroup.getRawValue();
    const selected = rawValue.lossReason;
    const idLossReason = typeof selected === 'string' ? selected : selected?._id;

    if (!idLossReason) {
      this.alertService.warning(this.t('kanban.selectLossReason.selectWarning'));
      this.loading = false;
      return;
    }

    // A platform discard demands the note (E3 RF-24); the API refuses without it on every path.
    const lossNote = `${rawValue.lossNote ?? ''}`.trim();
    if (this.selectedIsPlatform && !lossNote) {
      this.formGroup.get('lossNote')?.markAsTouched();
      this.alertService.warning(this.t('kanban.selectLossReason.noteRequired'));
      this.loading = false;
      return;
    }

    this.dialogRef.close(<SelectLossReasonResult>{ idLossReason, lossNote: this.selectedIsPlatform ? lossNote : undefined });
  }

  createLossReason(): void {
    const dialogRef = this.dialog.open(LossReasonFormModalComponent, {
      width: '600px',
      data: null
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: LossReason) => {
      if (result) {
        this.getList();
        this.formGroup.get('lossReason')?.setValue(result._id);
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
